import { IGetEscalatedDataFilter, IGetEscalatedDataPayload, ISortObj, TFilterType } from "../../../../Models/applicationModels";
import { EscalationFilters, IAvailableCreatedBy, IAvailableEscalation, IAvailableEscalationStatus } from "../../../../Models/escalatedPageModels";
import { DATE_FILTER_KEYS, EQUAL_OPERATOR, IN_OPERATOR } from "./escalatedPageHeader.constants";
import { CUSTOM_START_DATE_KEY, CUSTOM_END_DATE_KEY, IS_MAIL_SENT_KEY } from "../../escalatedPage.constants";
import { getDateRangeFromCreatedAt } from "../../../../utils/getFormattedTime";
import { GetTimeZoneName } from "../../../../utils/getTimezoneOffset";

const getEscalationFilterOperator = (key: string): TFilterType => {
    switch (key) {
        case IS_MAIL_SENT_KEY:
            return EQUAL_OPERATOR;
        default:
            return IN_OPERATOR;
    }
};

export const buildPayload = ({
    filters,
    pageSize,
    pageIndex,
    searchParam,
    sort,
}: {
    filters: EscalationFilters;
    pageSize: number;
    pageIndex: number;
    searchParam: string | null;
    sort: ISortObj | null;
}): IGetEscalatedDataPayload => {
    const { startDate, endDate } = getDateRangeFromCreatedAt(
        filters.createdAt,
        filters.customStartDate,
        filters.customEndDate
    );
    const filtersForApi = buildEscalationFiltersForPayload(
        filters,
        getEscalationFilterOperator
    );
    return {
        timezone: GetTimeZoneName(),
        sort,
        pageSize,
        pageIndex,
        startDate,
        endDate,    
        dayCount: null,
        searchParam,
        filters: filtersForApi.length > 0 ? filtersForApi : null,
    };
}

const buildEscalationFiltersForPayload = (
    filters: EscalationFilters,
    getFilterOperator: (key: string) => TFilterType
): IGetEscalatedDataFilter[] => {
    return Object.entries(filters)
        .filter(([key]) => !DATE_FILTER_KEYS.has(key))
        .filter(([, value]) => isValidFilterValue(value))
        .map(([key, value]) => {
            let resolvedValue = value as string | string[] | boolean;
            if(Array.isArray(value)) {
                resolvedValue = value.join(",");
            }
            return {
                field: key,
                operator: getFilterOperator(key),
                value: resolvedValue,
            };
        });
};

export const buildEscalationOptions = (escalationList?: IAvailableEscalation[]) => escalationList?.map(escalation => ({
        label: escalation.escalationName,
        value: escalation.escalationUid,
    })) || [];

export const buildStatusOptions = (statusList?: IAvailableEscalationStatus[]) =>
    statusList?.map(status => ({
        label: status.name,
        value: status.id.toString(),
    })) || [];

export const buildCreatedByOptions = (
    createdByList: IAvailableCreatedBy[] | undefined,
    devId: string | number
) => {
    if (!createdByList) return [];
    const devIdStr = `${devId}`;
    const found = createdByList.find(user => user.userId.toString() === devIdStr);
    const options = createdByList.map(user => ({
        label: user.userName,
        value: user.userId
    }));
    if (found) {
        const filteredOptions = options.filter(option => option.value.toString() !== devIdStr);
        return [
            { label: 'Me', value: devIdStr },
            ...filteredOptions
        ];
    } else {
        return options;
    }
};

const isValidFilterValue = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value;
    return !!value;
};

const notCountingFilters = new Set<string>([CUSTOM_START_DATE_KEY, CUSTOM_END_DATE_KEY]);

export const countActiveFilters = (filters: EscalationFilters): number =>
    Object.entries(filters)
        .filter(([key]) => !notCountingFilters.has(key))
        .reduce((count, [, value]) => 
            isValidFilterValue(value) ? count + 1 : count
        , 0);

